import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Sliders, Plus, Save, Trash2, Copy, FileDown, Loader2, Sparkles,
  TrendingUp, TrendingDown, X, FileSpreadsheet, FileText, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// UI option list. Each maps to a backend `adjustment_type` plus a `direction`
// (only relevant for shift options where positive = push later, negative = bring forward).
const UI_OPTIONS = [
  // ----- Invoices (AR) -----
  { id: 'bring_invoices_fwd',  label: 'Bring specific invoices forward',     group: 'Invoices (AR)', type: 'invoices',       direction: 'fwd', needs: 'invoices' },
  { id: 'push_invoices_out',   label: 'Push specific invoices out',          group: 'Invoices (AR)', type: 'invoices',       direction: 'out', needs: 'invoices' },
  { id: 'bring_all_invoices',  label: 'Bring ALL invoices forward',          group: 'Invoices (AR)', type: 'global_ar',      direction: 'fwd', needs: 'none' },
  { id: 'push_all_invoices',   label: 'Push ALL invoices out',               group: 'Invoices (AR)', type: 'global_ar',      direction: 'out', needs: 'none' },
  // ----- Bills (AP) -----
  { id: 'bring_bills_fwd',     label: 'Bring specific bills forward',        group: 'Bills (AP)',    type: 'bills',          direction: 'fwd', needs: 'bills' },
  { id: 'push_bills_out',      label: 'Push specific bills out',             group: 'Bills (AP)',    type: 'bills',          direction: 'out', needs: 'bills' },
  { id: 'bring_all_bills',     label: 'Bring ALL bills forward',             group: 'Bills (AP)',    type: 'global_ap',      direction: 'fwd', needs: 'none' },
  { id: 'push_all_bills',      label: 'Push ALL bills out',                  group: 'Bills (AP)',    type: 'global_ap',      direction: 'out', needs: 'none' },
  // ----- Other cash inflows -----
  { id: 'bring_inflows_fwd',   label: 'Bring specific cash inflows forward', group: 'Cash inflows',  type: 'inflows',        direction: 'fwd', needs: 'inflows' },
  { id: 'push_inflows_out',    label: 'Push specific cash inflows out',      group: 'Cash inflows',  type: 'inflows',        direction: 'out', needs: 'inflows' },
  { id: 'bring_all_inflows',   label: 'Bring ALL cash inflows forward',      group: 'Cash inflows',  type: 'global_inflow',  direction: 'fwd', needs: 'none' },
  { id: 'push_all_inflows',    label: 'Push ALL cash inflows out',           group: 'Cash inflows',  type: 'global_inflow',  direction: 'out', needs: 'none' },
  // ----- Other cash outflows -----
  { id: 'bring_outflows_fwd',  label: 'Bring specific cash outflows forward',group: 'Cash outflows', type: 'outflows',       direction: 'fwd', needs: 'outflows' },
  { id: 'push_outflows_out',   label: 'Push specific cash outflows out',     group: 'Cash outflows', type: 'outflows',       direction: 'out', needs: 'outflows' },
  { id: 'bring_all_outflows',  label: 'Bring ALL cash outflows forward',     group: 'Cash outflows', type: 'global_outflow', direction: 'fwd', needs: 'none' },
  { id: 'push_all_outflows',   label: 'Push ALL cash outflows out',          group: 'Cash outflows', type: 'global_outflow', direction: 'out', needs: 'none' },
  // ----- Capex -----
  { id: 'add_capex',           label: 'Add Capex (pick a specific date)',    group: 'Capex',         type: 'capex',          direction: null,  needs: 'capex' },
];

// Given a saved backend adjustment, find its UI option id by matching
// (adjustment_type, sign-of-extra_days_offset). Capex always uses `add_capex`.
const uiOptionFromAdjustment = (adj) => {
  if (adj.adjustment_type === 'capex') return 'add_capex';
  const direction = (adj.extra_days_offset ?? 0) < 0 ? 'fwd' : 'out';
  const match = UI_OPTIONS.find(
    (o) => o.type === adj.adjustment_type && o.direction === direction,
  );
  return match ? match.id : UI_OPTIONS[0].id;
};

const ADJUSTMENT_OPTION_BY_ID = Object.fromEntries(UI_OPTIONS.map((o) => [o.id, o]));

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })
    .format(Number(n) || 0);

const emptyScenario = () => ({
  name: 'New scenario',
  description: '',
  color: DEFAULT_COLORS[0],
  adjustments: [{ adjustment_type: 'global_ar', target_id: null, extra_days_offset: -30 }],
});

export default function ScenariosPage() {
  return (
    <Layout>
      <ScenariosContent />
    </Layout>
  );
}

function ScenariosContent() {
  const { activeBusiness } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [implementOpen, setImplementOpen] = useState(false);
  const [implementing, setImplementing] = useState(false);
  const [forecasts, setForecasts] = useState({}); // { scenarioId: forecast }
  const [chartIds, setChartIds] = useState([]); // scenario ids to overlay
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [otherCash, setOtherCash] = useState([]);

  // Date-range filter on the forecast chart + table (mirrors Dashboard's From/To).
  // Defaults to the Dashboard's saved range (localStorage key `cf:daily-range:<bizId>`)
  // OR, if none saved, the first 30 days of the forecast window — same logic as
  // DashboardPage so both views start on the same window by default.
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  // Daily series for the chart — fetched from the new /forecast-daily endpoint
  // so the Scenarios chart matches the Dashboard daily chart day-by-day.
  // Keyed by `${scenarioId}::${rangeStart}::${rangeEnd}` to avoid duplicate fetches.
  const [dailySeries, setDailySeries] = useState({});
  const [dailyLoading, setDailyLoading] = useState(false);

  // Live preview of the draft adjustments (not yet saved). When set, the chart
  // overlays a dashed "Preview" line computed from the current, un-persisted
  // editor state. Cleared whenever the user Saves or switches scenario.
  const [previewDaily, setPreviewDaily] = useState(null);  // {days: [...]}
  const [previewing, setPreviewing] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, s, inv, bil, oc] = await Promise.all([
        axios.get(`${API}/scenarios`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/bills`),
        axios.get(`${API}/other-cash`),
      ]);
      setScenarios(r.data || []);
      setCustomers(c.data || []);
      setSuppliers(s.data || []);
      setInvoices(inv.data || []);
      setBills(bil.data || []);
      setOtherCash(oc.data || []);
      if ((r.data || []).length && !selectedId) {
        setSelectedId(r.data[0].id);
        setDraft({ ...r.data[0] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadList(); }, [loadList, activeBusiness?.id]);

  // Load forecast for selected scenario
  const loadForecast = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/scenarios/${id}/forecast`);
      setForecasts((prev) => ({ ...prev, [id]: r.data.forecast }));
    } catch (e) {
      toast.error('Could not load forecast');
    }
  }, []);
  useEffect(() => {
    if (selectedId) {
      loadForecast(selectedId);
      setChartIds((prev) => (prev.includes(selectedId) ? prev : [...prev, selectedId]));
    }
  }, [selectedId, loadForecast]);

  const selectScenario = (id) => {
    setSelectedId(id);
    const sc = scenarios.find((x) => x.id === id);
    if (sc) setDraft({ ...sc });
  };

  const createNew = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/scenarios`, emptyScenario());
      setScenarios((prev) => [r.data, ...prev]);
      setSelectedId(r.data.id);
      setDraft({ ...r.data });
      toast.success('Scenario created');
    } catch (e) {
      toast.error('Create failed');
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!draft || !selectedId) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/scenarios/${selectedId}`, {
        name: draft.name,
        description: draft.description,
        color: draft.color,
        adjustments: draft.adjustments,
      });
      setScenarios((prev) => prev.map((s) => (s.id === selectedId ? r.data : s)));
      toast.success('Saved');
      setPreviewDaily(null);
      // Blow away cached daily series for this scenario so the chart refetches
      setDailySeries((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => { if (k.startsWith(`${selectedId}::`)) delete next[k]; });
        return next;
      });
      await loadForecast(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Preview — fetch the daily forecast for the draft adjustments WITHOUT saving.
  // Renders as a dashed "Preview" line overlay on the chart.
  const previewDraft = async () => {
    if (!draft || !rangeStart || !rangeEnd) return;
    setPreviewing(true);
    try {
      const r = await axios.post(`${API}/scenarios/preview/daily`, {
        adjustments: draft.adjustments || [],
        start: rangeStart,
        end: rangeEnd,
      });
      setPreviewDaily(r.data);
      if ((r.data?.days?.length || 0) === 0) {
        toast.info('No forecast days in this range yet — try pushing the From date to today or later');
      } else {
        toast.success('Preview updated');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  // Clear preview whenever the user switches scenario or edits the date range
  useEffect(() => { setPreviewDaily(null); }, [selectedId, rangeStart, rangeEnd]);

  const implementScenario = async () => {
    if (!selectedId) return;
    setImplementing(true);
    try {
      const r = await axios.post(`${API}/scenarios/${selectedId}/implement`);
      const d = r.data;
      const bits = [];
      if (d.invoices_updated) bits.push(`${d.invoices_updated} invoices`);
      if (d.bills_updated) bits.push(`${d.bills_updated} bills`);
      if (d.other_cash_updated) bits.push(`${d.other_cash_updated} other-cash dates`);
      if (d.other_cash_inserted) bits.push(`${d.other_cash_inserted} new capex rows`);
      const summary = bits.length ? bits.join(', ') : 'no changes (already in this state)';
      toast.success(`Implemented · ${summary}`);
      setImplementOpen(false);
      const r2 = await axios.get(`${API}/scenarios`);
      setScenarios(r2.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Implement failed');
    } finally {
      setImplementing(false);
    }
  };


  const duplicateScenario = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/scenarios/${selectedId}/duplicate`);
      setScenarios((prev) => [r.data, ...prev]);
      setSelectedId(r.data.id);
      setDraft({ ...r.data });
      toast.success('Duplicated');
    } catch (e) {
      toast.error('Duplicate failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteScenario = async () => {
    if (!selectedId) return;
    if (!window.confirm('Delete this scenario? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/scenarios/${selectedId}`);
      const remaining = scenarios.filter((s) => s.id !== selectedId);
      setScenarios(remaining);
      setChartIds((prev) => prev.filter((x) => x !== selectedId));
      if (remaining.length) {
        setSelectedId(remaining[0].id);
        setDraft({ ...remaining[0] });
      } else {
        setSelectedId(null);
        setDraft(null);
      }
      toast.success('Scenario deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const exportFile = (fmt) => {
    if (!selectedId) return;
    const url = `${API}/scenarios/${selectedId}/export.${fmt}`;
    // Use fetch + blob so the auth header is sent
    axios.get(url, { responseType: 'blob' }).then((r) => {
      const blob = new Blob([r.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = (draft?.name || 'scenario').replace(/\s+/g, '_');
      link.download = `${name}.${fmt}`;
      link.click();
    }).catch(() => toast.error('Export failed'));
  };

  const updateAdjustment = (idx, patch) => {
    setDraft((prev) => ({
      ...prev,
      adjustments: prev.adjustments.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  };
  // Switch a row to a different UI option — translates to backend adjustment shape.
  const setUiOption = (idx, optionId) => {
    const opt = ADJUSTMENT_OPTION_BY_ID[optionId];
    if (!opt) return;
    const current = (draft.adjustments || [])[idx] || {};
    // Preserve magnitude of days; flip sign per direction.
    const days = Math.abs(Number(current.extra_days_offset || 14)) || 14;
    const signed = opt.direction === 'fwd' ? -days : days;
    const next = {
      adjustment_type: opt.type,
      target_id: null,
      target_ids: null,
      extra_days_offset: opt.type === 'capex' ? 0 : signed,
    };
    if (opt.type === 'capex') {
      next.capex_date = current.capex_date || new Date().toISOString().slice(0, 10);
      next.capex_amount = current.capex_amount || 10000;
      next.capex_description = current.capex_description || '';
    }
    updateAdjustment(idx, next);
  };
  // When the user types in the days field — store the signed value based on direction.
  const setDays = (idx, optionId, rawMagnitude) => {
    const opt = ADJUSTMENT_OPTION_BY_ID[optionId];
    const magnitude = Math.abs(parseInt(rawMagnitude || '0', 10)) || 0;
    const signed = opt && opt.direction === 'fwd' ? -magnitude : magnitude;
    updateAdjustment(idx, { extra_days_offset: signed });
  };
  const addAdjustment = () => {
    setDraft((prev) => ({
      ...prev,
      adjustments: [...(prev.adjustments || []), { adjustment_type: 'global_ar', extra_days_offset: -30 }],
    }));
  };
  const removeAdjustment = (idx) => {
    setDraft((prev) => ({
      ...prev,
      adjustments: prev.adjustments.filter((_, i) => i !== idx),
    }));
  };

  // Build a multi-line chart series from selected scenario forecasts
  // (filtered by the optional date range — defaults to full 13 months).
  const selectedForecastSrc = forecasts[selectedId];

  // Min / Max month_key in the selected forecast — used to bound the date inputs
  const { minMonthKey, maxMonthKey } = useMemo(() => {
    const rows = selectedForecastSrc?.rows || [];
    if (!rows.length) return { minMonthKey: '', maxMonthKey: '' };
    return { minMonthKey: rows[0].month_key, maxMonthKey: rows[rows.length - 1].month_key };
  }, [selectedForecastSrc]);

  // Sync default range with the Dashboard:
  //  1. If localStorage has `cf:daily-range:<bizId>` (set by the Dashboard), use it.
  //  2. Otherwise default to the first 30 days of the forecast window — same
  //     fallback as DashboardPage so both pages naturally start aligned.
  // Runs once per (business, forecast) pair so it doesn't fight user edits.
  const rangeInitialisedRef = React.useRef(null);
  useEffect(() => {
    if (!activeBusiness?.id || !selectedForecastSrc?.rows?.length) return;
    const key = `${activeBusiness.id}::${selectedId}`;
    if (rangeInitialisedRef.current === key) return;
    rangeInitialisedRef.current = key;

    const startISO = selectedForecastSrc.opening_balance_date
      || selectedForecastSrc.rows[0].month_key;
    const lastMonthStart = selectedForecastSrc.rows[selectedForecastSrc.rows.length - 1].month_key;
    // Compute end-of-last-month for the input max
    const [ly, lm] = lastMonthStart.split('-').map(Number);
    const eom = new Date(Date.UTC(ly, lm, 0)); // day 0 of next month → last day of lm
    const endISO = eom.toISOString().slice(0, 10);

    let resolvedStart = '';
    let resolvedEnd = '';
    try {
      const saved = JSON.parse(localStorage.getItem(`cf:daily-range:${activeBusiness.id}`) || 'null');
      if (saved?.start && saved?.end && saved.start >= startISO && saved.end <= endISO) {
        resolvedStart = saved.start;
        resolvedEnd = saved.end;
      }
    } catch { /* ignore */ }

    if (!resolvedStart) {
      const s = new Date(startISO + 'T00:00:00');
      const d = new Date(s);
      d.setDate(d.getDate() + 29);
      const max = new Date(endISO + 'T00:00:00');
      const final = d > max ? max : d;
      resolvedStart = startISO;
      resolvedEnd = final.toISOString().slice(0, 10);
    }
    setRangeStart(resolvedStart);
    setRangeEnd(resolvedEnd);
  }, [activeBusiness?.id, selectedId, selectedForecastSrc]);

  // Persist whatever range the user chooses on the Scenarios page back to the
  // SAME key the Dashboard reads from — so changes in either page sync to the other.
  useEffect(() => {
    if (!rangeStart || !rangeEnd || !activeBusiness?.id) return;
    try {
      localStorage.setItem(
        `cf:daily-range:${activeBusiness.id}`,
        JSON.stringify({ start: rangeStart, end: rangeEnd })
      );
    } catch { /* ignore quota */ }
  }, [rangeStart, rangeEnd, activeBusiness?.id]);

  // Inclusive month-level filter. `rangeStart` / `rangeEnd` are 'YYYY-MM-DD' from
  // <input type="date">. Any day within a month selects that whole month row.
  const inRange = useCallback((mk) => {
    if (!mk) return true;
    // Normalize a date input to its month-start key (YYYY-MM-01) for comparison
    const monthStart = (iso) => (iso ? `${iso.slice(0, 7)}-01` : '');
    const lo = monthStart(rangeStart);
    const hi = monthStart(rangeEnd);
    if (lo && mk < lo) return false;
    if (hi && mk > hi) return false;
    return true;
  }, [rangeStart, rangeEnd]);

  const filteredRows = useMemo(() => {
    const rows = selectedForecastSrc?.rows || [];
    return rows.filter((r) => inRange(r.month_key));
  }, [selectedForecastSrc, inRange]);

  const filteredForecast = useMemo(() => {
    if (!selectedForecastSrc) return null;
    return { ...selectedForecastSrc, rows: filteredRows };
  }, [selectedForecastSrc, filteredRows]);

  // Fetch daily series for the selected scenario AND every overlaid scenario,
  // whenever the date range changes. Cached client-side per (scenarioId, start, end).
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const ids = new Set([selectedId, ...chartIds].filter(Boolean));
    if (!ids.size) return;
    let cancelled = false;
    setDailyLoading(true);
    (async () => {
      try {
        await Promise.all([...ids].map(async (id) => {
          const cacheKey = `${id}::${rangeStart}::${rangeEnd}`;
          if (dailySeries[cacheKey]) return;
          try {
            const res = await axios.get(`${API}/scenarios/${id}/forecast-daily`, {
              params: { start: rangeStart, end: rangeEnd },
            });
            if (!cancelled) {
              setDailySeries((prev) => ({ ...prev, [cacheKey]: res.data }));
            }
          } catch (e) {
            // swallow — chart will simply omit that series
          }
        }));
      } finally {
        if (!cancelled) setDailyLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd, selectedId, chartIds]);

  // Daily chart series — one point per day, aligned across scenarios.
  // Replaces the old monthly-step chartData so the Scenarios chart visually
  // matches the Dashboard daily chart exactly.
  const chartData = useMemo(() => {
    if (!rangeStart || !rangeEnd || !selectedId) return [];
    const baseKey = `${selectedId}::${rangeStart}::${rangeEnd}`;
    const baseDaily = dailySeries[baseKey];
    if (!baseDaily?.days?.length) return [];
    return baseDaily.days.map((d) => {
      // Compact label: "30 Jun" / "01 Jul"
      const [y, m, day] = d.date.split('-').map(Number);
      const label = new Date(Date.UTC(y, m - 1, day)).toLocaleString('en-US', {
        day: '2-digit', month: 'short', timeZone: 'UTC',
      });
      const point = { date: d.date, label, Baseline: d.baseline_close };
      chartIds.forEach((id) => {
        const sc = scenarios.find((s) => s.id === id);
        if (!sc) return;
        const overlay = dailySeries[`${id}::${rangeStart}::${rangeEnd}`];
        const row = overlay?.days?.find((r) => r.date === d.date);
        if (row) point[sc.name] = row.scenario_close;
      });
      // Preview overlay (dashed) for unsaved edits on the currently selected scenario
      if (previewDaily?.days?.length) {
        const prow = previewDaily.days.find((r) => r.date === d.date);
        if (prow) point.Preview = prow.scenario_close;
      }
      return point;
    });
  }, [dailySeries, selectedId, chartIds, scenarios, rangeStart, rangeEnd, previewDaily]);

  const clearRange = () => { setRangeStart(''); setRangeEnd(''); };

  // Make sure forecasts are loaded for every selected chart id
  useEffect(() => {
    chartIds.forEach((id) => {
      if (!forecasts[id]) loadForecast(id);
    });
  }, [chartIds, forecasts, loadForecast]);

  const selectedForecast = selectedId ? forecasts[selectedId] : null;

  if (activeBusiness?.is_consolidated) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="max-w-md text-center space-y-3" data-testid="consolidated-blocked-scenarios">
          <Sliders className="w-10 h-10 text-slate-300 mx-auto" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Scenarios are per-business
          </h2>
          <p className="text-sm text-slate-500">
            Switch to a specific business from the sidebar to create or run scenarios.
            Consolidated view shows the baseline across all your businesses.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="scenarios-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Scenarios
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Model what-if cash-flow shifts. Build, save, and compare multiple scenarios side-by-side.
          </p>
        </div>
        <Button onClick={createNew} disabled={saving} className="gap-1.5" data-testid="scenario-new-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New scenario
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Left list */}
        <Card className="self-start" data-testid="scenario-list-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sliders className="w-4 h-4" />
              Saved scenarios
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-2">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : scenarios.length === 0 ? (
              <div className="text-center py-6 px-2 text-xs text-slate-500">
                <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                No scenarios yet. Click "New scenario" to start.
              </div>
            ) : (
              <ul className="space-y-1">
                {scenarios.map((s) => {
                  const active = s.id === selectedId;
                  const inChart = chartIds.includes(s.id);
                  return (
                    <li key={s.id}>
                      <div
                        className={`group flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer transition ${
                          active
                            ? 'bg-primary/10 ring-1 ring-primary/30'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                        onClick={() => selectScenario(s.id)}
                        data-testid={`scenario-item-${s.id}`}
                      >
                        <span
                          className="w-3 h-3 rounded-sm shrink-0"
                          style={{ backgroundColor: s.color || '#3b82f6' }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate text-slate-900 dark:text-slate-100">
                            {s.name}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {(s.adjustments || []).length} adjustment{(s.adjustments || []).length === 1 ? '' : 's'} · {s.created_by_name}
                          </div>
                          {s.last_implemented_at && (
                            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium truncate flex items-center gap-0.5 mt-0.5">
                              <CheckCircle2 className="w-2.5 h-2.5" />
                              Implemented {new Date(s.last_implemented_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <label
                          className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                          title="Show in comparison chart"
                        >
                          <Checkbox
                            checked={inChart}
                            onCheckedChange={(v) => {
                              setChartIds((prev) =>
                                v ? [...new Set([...prev, s.id])] : prev.filter((x) => x !== s.id)
                              );
                            }}
                            data-testid={`scenario-chart-toggle-${s.id}`}
                          />
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Right editor + preview */}
        <div className="space-y-4 min-w-0">
          {!draft ? (
            <Card>
              <CardContent className="py-16 text-center text-slate-500">
                <Sparkles className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                Select a scenario from the left, or create a new one to get started.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card data-testid="scenario-editor-card">
                <CardHeader className="py-3 flex flex-row items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base">Editor</CardTitle>
                    <CardDescription className="text-xs">
                      Define one or more cash-flow adjustments. The forecast below recomputes when you Save.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Button variant="outline" size="sm" onClick={duplicateScenario} disabled={saving} data-testid="scenario-duplicate-btn">
                      <Copy className="w-3.5 h-3.5 mr-1" />
                      Duplicate
                    </Button>
                    <Button variant="outline" size="sm" onClick={deleteScenario} className="text-rose-600 hover:bg-rose-50" data-testid="scenario-delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                    <Button size="sm" onClick={previewDraft} disabled={previewing || !rangeStart || !rangeEnd} variant="outline" className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="scenario-preview-btn" title="See the forecast impact of your current edits without saving">
                      {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      Preview
                    </Button>
                    <Button size="sm" onClick={saveDraft} disabled={saving} variant="outline" className="gap-1.5" data-testid="scenario-save-btn">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setImplementOpen(true)}
                      disabled={saving || implementing}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="scenario-implement-btn"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Implement scenario
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
                      <Input
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        data-testid="scenario-name-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Chart colour</Label>
                      <div className="flex items-center gap-1 flex-wrap">
                        {DEFAULT_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setDraft({ ...draft, color: c })}
                            className={`w-6 h-6 rounded ${draft.color === c ? 'ring-2 ring-offset-1 ring-primary' : 'hover:ring-1 hover:ring-slate-300'}`}
                            style={{ backgroundColor: c }}
                            data-testid={`scenario-color-${c}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Description (optional)</Label>
                    <Textarea
                      rows={2}
                      value={draft.description || ''}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      placeholder="e.g. Big customer extends terms to 60 days, supplier negotiations push AP out by 10 days"
                      data-testid="scenario-description-input"
                    />
                  </div>

                  {/* Adjustments */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Adjustments</Label>
                      <Button variant="ghost" size="sm" onClick={addAdjustment} className="h-7 gap-1 text-primary" data-testid="scenario-add-adj">
                        <Plus className="w-3.5 h-3.5" /> Add another
                      </Button>
                    </div>
                    {(draft.adjustments || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No adjustments yet — click "Add another" above.</p>
                    )}
                    <div className="space-y-2">
                      {(draft.adjustments || []).map((adj, idx) => {
                        const optionId = uiOptionFromAdjustment(adj);
                        const opt = ADJUSTMENT_OPTION_BY_ID[optionId] || UI_OPTIONS[0];
                        const isCapex = opt.type === 'capex';
                        const isForward = opt.direction === 'fwd';
                        const daysMagnitude = Math.abs(adj.extra_days_offset || 0);
                        return (
                          <div
                            key={idx}
                            className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/50 dark:bg-slate-900/40"
                            data-testid={`scenario-adj-${idx}`}
                          >
                            <div className="flex items-end gap-2 flex-wrap">
                              <div className="space-y-1 flex-1 min-w-[260px]">
                                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Type</Label>
                                <Select
                                  value={optionId}
                                  onValueChange={(v) => setUiOption(idx, v)}
                                >
                                  <SelectTrigger className="h-9" data-testid={`scenario-adj-type-${idx}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[420px]">
                                    {Array.from(new Set(UI_OPTIONS.map((o) => o.group))).map((group) => (
                                      <React.Fragment key={group}>
                                        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-1">{group}</div>
                                        {UI_OPTIONS.filter((o) => o.group === group).map((o) => (
                                          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {opt.needs === 'invoices' && (
                                <MultiSelectPicker
                                  testIdPrefix={`scenario-adj-invoices-${idx}`}
                                  label="Invoices"
                                  options={invoices.map((i) => ({
                                    id: i.id,
                                    label: `${i.invoice_number || '—'} · ${(customers.find((c) => c.id === i.customer_id) || {}).name || 'Unknown'} · ${fmtMoney(i.amount)}`,
                                  }))}
                                  values={adj.target_ids || []}
                                  onChange={(ids) => updateAdjustment(idx, { target_ids: ids })}
                                />
                              )}
                              {opt.needs === 'bills' && (
                                <MultiSelectPicker
                                  testIdPrefix={`scenario-adj-bills-${idx}`}
                                  label="Bills"
                                  options={bills.map((b) => ({
                                    id: b.id,
                                    label: `${b.bill_number || '—'} · ${(suppliers.find((s) => s.id === b.supplier_id) || {}).name || 'Unknown'} · ${fmtMoney(b.amount)}`,
                                  }))}
                                  values={adj.target_ids || []}
                                  onChange={(ids) => updateAdjustment(idx, { target_ids: ids })}
                                />
                              )}
                              {opt.needs === 'inflows' && (
                                <MultiSelectPicker
                                  testIdPrefix={`scenario-adj-inflows-${idx}`}
                                  label="Inflows"
                                  options={otherCash
                                    .filter((o) => Number(o.amount) > 0)
                                    .map((o) => ({ id: o.id, label: `${o.cash_date} · ${o.description || 'Inflow'} · ${fmtMoney(o.amount)}` }))}
                                  values={adj.target_ids || []}
                                  onChange={(ids) => updateAdjustment(idx, { target_ids: ids })}
                                />
                              )}
                              {opt.needs === 'outflows' && (
                                <MultiSelectPicker
                                  testIdPrefix={`scenario-adj-outflows-${idx}`}
                                  label="Outflows"
                                  options={otherCash
                                    .filter((o) => Number(o.amount) < 0)
                                    .map((o) => ({ id: o.id, label: `${o.cash_date} · ${o.description || 'Outflow'} · ${fmtMoney(Math.abs(o.amount))}` }))}
                                  values={adj.target_ids || []}
                                  onChange={(ids) => updateAdjustment(idx, { target_ids: ids })}
                                />
                              )}

                              {/* Capex: date + amount + description, NO days field */}
                              {isCapex ? (
                                <>
                                  <div className="space-y-1">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Capex date</Label>
                                    <Input
                                      type="date"
                                      value={adj.capex_date || ''}
                                      onChange={(e) => updateAdjustment(idx, { capex_date: e.target.value })}
                                      className="h-9 w-40 font-mono"
                                      data-testid={`scenario-adj-capex-date-${idx}`}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={adj.capex_amount ?? ''}
                                      onChange={(e) => updateAdjustment(idx, { capex_amount: parseFloat(e.target.value || '0') })}
                                      className="h-9 w-32 font-mono"
                                      data-testid={`scenario-adj-capex-amount-${idx}`}
                                    />
                                  </div>
                                  <div className="space-y-1 flex-1 min-w-[160px]">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Description (optional)</Label>
                                    <Input
                                      type="text"
                                      placeholder="e.g. New oven"
                                      value={adj.capex_description || ''}
                                      onChange={(e) => updateAdjustment(idx, { capex_description: e.target.value })}
                                      className="h-9"
                                      data-testid={`scenario-adj-capex-desc-${idx}`}
                                    />
                                  </div>
                                </>
                              ) : (
                                <div className="space-y-1">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                                    Days {isForward ? 'forward' : 'out'}
                                  </Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={daysMagnitude}
                                    onChange={(e) => setDays(idx, optionId, e.target.value)}
                                    className="h-9 w-24 font-mono"
                                    data-testid={`scenario-adj-days-${idx}`}
                                  />
                                </div>
                              )}

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAdjustment(idx)}
                                className="h-9 w-9 p-0 text-slate-400 hover:text-rose-600"
                                data-testid={`scenario-adj-remove-${idx}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1.5">
                              {isCapex
                                ? 'A one-off capital outflow on the chosen date — applied to the scenario forecast only.'
                                : isForward
                                ? `Brings the targeted payment dates ${daysMagnitude || 0} day${daysMagnitude === 1 ? '' : 's'} forward (earlier).`
                                : `Pushes the targeted payment dates ${daysMagnitude || 0} day${daysMagnitude === 1 ? '' : 's'} out (later).`}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Forecast chart + table */}
              <Card data-testid="scenario-forecast-card">
                <CardHeader className="py-3 flex flex-row items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-base">Forecast comparison</CardTitle>
                    <CardDescription className="text-xs">
                      Cumulative cash position. Toggle scenarios in the left list to overlay them on the chart.
                    </CardDescription>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => exportFile('csv')} data-testid="scenario-export-csv">
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportFile('xlsx')} data-testid="scenario-export-xlsx">
                      <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                      Excel
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedForecast ? (
                    <div className="py-12 flex items-center justify-center text-slate-400">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Date-range filter — mirrors the dashboard's From/To */}
                      <div className="flex flex-wrap items-end gap-2 mb-3 text-xs">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                          <Input
                            type="date"
                            value={rangeStart}
                            min={minMonthKey || undefined}
                            max={maxMonthKey || undefined}
                            onChange={(e) => setRangeStart(e.target.value)}
                            className="h-8 w-[150px]"
                            data-testid="scenario-range-from"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                          <Input
                            type="date"
                            value={rangeEnd}
                            min={minMonthKey || undefined}
                            max={maxMonthKey || undefined}
                            onChange={(e) => setRangeEnd(e.target.value)}
                            className="h-8 w-[150px]"
                            data-testid="scenario-range-to"
                          />
                        </div>
                        {(rangeStart || rangeEnd) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearRange}
                            className="h-8 text-xs"
                            data-testid="scenario-range-clear"
                          >
                            Reset (show all months)
                          </Button>
                        )}
                        <span className="text-[11px] text-slate-500 ml-auto">
                          {dailyLoading
                            ? 'Loading daily series…'
                            : `Showing ${chartData.length} days • Table: ${filteredRows.length} of ${selectedForecastSrc?.rows?.length || 0} months`}
                        </span>
                      </div>

                      <div className="h-72 w-full" data-testid="scenario-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
                            <XAxis
                              dataKey="label"
                              tick={{ fontSize: 10 }}
                              interval={Math.max(0, Math.floor(chartData.length / 12))}
                            />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtMoney} />
                            <Tooltip formatter={(v) => fmtMoney(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {/* Zero balance reference */}
                            <ReferenceLine
                              y={0}
                              stroke="#ef4444"
                              strokeDasharray="6 3"
                              strokeWidth={1.5}
                              label={{ value: 'Zero balance', position: 'insideTopLeft', fill: '#ef4444', fontSize: 10 }}
                            />
                            {/* Threshold (from Dashboard Settings → Cashflow threshold) */}
                            {selectedForecast?.cashflow_threshold > 0 && (
                              <ReferenceLine
                                y={selectedForecast.cashflow_threshold}
                                stroke="#f59e0b"
                                strokeDasharray="4 4"
                                strokeWidth={1.5}
                                label={{
                                  value: `Threshold ${fmtMoney(selectedForecast.cashflow_threshold)}`,
                                  position: 'insideTopRight',
                                  fill: '#f59e0b',
                                  fontSize: 10,
                                }}
                              />
                            )}
                            <Line type="monotone" dataKey="Baseline" stroke="#64748b" strokeDasharray="4 4" strokeWidth={2} dot={false} />
                            {previewDaily && (
                              <Line
                                type="monotone"
                                dataKey="Preview"
                                stroke="#3b82f6"
                                strokeDasharray="2 4"
                                strokeWidth={2.5}
                                dot={false}
                              />
                            )}
                            {chartIds.map((id) => {
                              const sc = scenarios.find((s) => s.id === id);
                              if (!sc) return null;
                              return (
                                <Line
                                  key={id}
                                  type="monotone"
                                  dataKey={sc.name}
                                  stroke={sc.color || '#3b82f6'}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <ScenarioSummary forecast={selectedForecast} />
                      <ScenarioTable forecast={filteredForecast} />
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Implement confirmation */}
      <AlertDialog open={implementOpen} onOpenChange={setImplementOpen}>
        <AlertDialogContent data-testid="scenario-implement-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Implement scenario into Dashboard?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-sm">
              <span className="block">
                This will <strong>apply every adjustment</strong> in this scenario directly to your underlying records:
              </span>
              <ul className="list-disc pl-5 text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                <li>Invoices &amp; bills → <code>extra_days</code> updated (matches the new payment date)</li>
                <li>Cash inflows/outflows → <code>cash_date</code> shifted</li>
                <li>Capex → a new outflow row added to Other Cash</li>
              </ul>
              <span className="block text-amber-700 dark:text-amber-300 text-xs">
                The dashboard chart will then reflect these changes. You can undo by editing each record manually, or by implementing an opposite scenario.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scenario-implement-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={implementScenario}
              disabled={implementing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="scenario-implement-confirm"
            >
              {implementing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              Yes, implement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ScenarioSummary({ forecast }) {
  const t = forecast.totals;
  const netDelta = t.scenario_net - t.baseline_net;
  const cumDelta = t.ending_scenario_cumulative - t.ending_baseline_cumulative;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
      <SummaryTile
        label="Baseline ending cash"
        value={fmtMoney(t.ending_baseline_cumulative)}
        accent="slate"
      />
      <SummaryTile
        label="Scenario ending cash"
        value={fmtMoney(t.ending_scenario_cumulative)}
        accent={cumDelta >= 0 ? 'emerald' : 'rose'}
      />
      <SummaryTile
        label={cumDelta >= 0 ? 'Cash uplift' : 'Cash erosion'}
        value={`${cumDelta >= 0 ? '+' : ''}${fmtMoney(cumDelta)}`}
        accent={cumDelta >= 0 ? 'emerald' : 'rose'}
        icon={cumDelta >= 0 ? TrendingUp : TrendingDown}
      />
    </div>
  );
}

function SummaryTile({ label, value, accent, icon: Icon }) {
  const ring = {
    slate: 'border-slate-200',
    emerald: 'border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/20',
    rose: 'border-rose-200 bg-rose-50/60 dark:bg-rose-900/20',
  }[accent];
  const text = {
    slate: 'text-slate-900 dark:text-slate-100',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose: 'text-rose-700 dark:text-rose-300',
  }[accent];
  return (
    <div className={`rounded-md border p-3 ${ring}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </div>
      <div className={`text-xl font-mono font-semibold mt-1 ${text}`}>{value}</div>
    </div>
  );
}

function ScenarioTable({ forecast }) {  return (
    <div className="mt-4 overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500">Month</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Baseline net</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Scenario net</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Δ</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Baseline cum.</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Scenario cum.</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {forecast.rows.map((r) => (
            <TableRow key={r.month_key}>
              <TableCell className="text-sm font-mono">{r.month} {r.year}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtMoney(r.baseline_net)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtMoney(r.scenario_net)}</TableCell>
              <TableCell className={`text-right font-mono text-xs ${r.delta_net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {r.delta_net >= 0 ? '+' : ''}{fmtMoney(r.delta_net)}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">{fmtMoney(r.baseline_cumulative)}</TableCell>
              <TableCell className="text-right font-mono text-xs font-semibold">{fmtMoney(r.scenario_cumulative)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MultiSelectPicker({ label, options, values, onChange, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => values.includes(o.id));

  const toggle = (id) => {
    if (values.includes(id)) onChange(values.filter((v) => v !== id));
    else onChange([...values, id]);
  };
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filtered.map((o) => o.id));
      onChange(values.filter((v) => !filteredIds.has(v)));
    } else {
      const merged = new Set([...values, ...filtered.map((o) => o.id)]);
      onChange([...merged]);
    }
  };

  return (
    <div className="space-y-1 flex-1 min-w-[260px] relative">
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-9 w-full text-left rounded-md border border-input bg-background px-3 text-sm flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800"
        data-testid={`${testIdPrefix}-trigger`}
      >
        <span className="truncate text-slate-600 dark:text-slate-300">
          {values.length === 0
            ? `Pick ${label.toLowerCase()}…`
            : `${values.length} selected`}
        </span>
        <X className={`w-3.5 h-3.5 shrink-0 ml-1 transition-transform ${open ? 'rotate-45' : 'rotate-0 opacity-50'}`} />
      </button>
      {open && (
        <div
          className="absolute z-30 top-full mt-1 left-0 w-[420px] max-w-[90vw] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-2"
          data-testid={`${testIdPrefix}-panel`}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter…"
              className="h-8 text-xs"
              data-testid={`${testIdPrefix}-search`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-[11px] shrink-0"
              onClick={toggleAllFiltered}
              data-testid={`${testIdPrefix}-toggle-all`}
            >
              {allFilteredSelected ? 'Clear visible' : 'Select all visible'}
            </Button>
          </div>
          <div className="max-h-56 overflow-y-auto rounded border border-slate-100 dark:border-slate-800">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-500 text-center">No matches.</p>
            ) : (
              filtered.map((o) => {
                const active = values.includes(o.id);
                return (
                  <label
                    key={o.id}
                    className={`flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${active ? 'bg-primary/5' : ''}`}
                  >
                    <Checkbox
                      checked={active}
                      onCheckedChange={() => toggle(o.id)}
                      data-testid={`${testIdPrefix}-item-${o.id}`}
                    />
                    <span className="truncate">{o.label}</span>
                  </label>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between pt-1.5 px-1 text-[11px] text-slate-500">
            <span>{values.length} selected · {filtered.length} shown / {options.length} total</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-primary font-medium hover:underline"
              data-testid={`${testIdPrefix}-close`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

